import { useEffect, useRef } from "react";
import { useToast } from "@/hooks/useToast";
import { UseFetchDataParams } from "@/types/index.type";
import { useQuery, QueryObserverSuccessResult } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

export const useFetchData = <TData = any, TError = any, TSelected = TData>({
  queryKey,
  queryFn,
  enableCondition = true,
  refetchOnMount = true,
  onSuccessFn,
  onErrorFn,
  selectFn,
}: UseFetchDataParams<TData, TError, TSelected>) => {
  const { toast } = useToast();
  const { t } = useTranslation();

  const data = useQuery<TData, TError, TSelected>({
    queryKey,
    queryFn,
    refetchOnWindowFocus: false,
    refetchOnMount: refetchOnMount ?? false,
    retry: 1,
    enabled: enableCondition,

    select: (data: TData) => {
      if (selectFn) {
        return selectFn(data);
      }
      return data as any;
    },
  });

  // Memoize the error message to avoid unnecessary re-renders
  const errorMessage = data.isError
    ? (data.error as Error)?.message || t("an_unexpected_error_occurred_please_try_again")
    : null;

  // Callers pass onSuccessFn/onErrorFn as fresh inline closures on every render. Putting the
  // whole query `data` object (a new reference nearly every render) or those closures in a
  // useEffect's dependency array reruns the effect on every unrelated re-render - and if the
  // effect itself causes a re-render (a toast() call does, via its own state), that is an
  // infinite loop: effect fires -> toast -> re-render -> new closure/data ref -> effect fires
  // again. Refs sidestep this: the effect keys only off dataUpdatedAt/errorUpdatedAt, which
  // change once per actual fetch, not once per render.
  const onSuccessRef = useRef(onSuccessFn);
  onSuccessRef.current = onSuccessFn;
  const onErrorRef = useRef(onErrorFn);
  onErrorRef.current = onErrorFn;

  useEffect(() => {
    if (data.isError && errorMessage) {
      toast({ title: errorMessage, variant: "destructive" });
      onErrorRef.current && onErrorRef.current(errorMessage);
    }
  }, [data.isError, errorMessage, data.errorUpdatedAt, toast]);

  useEffect(() => {
    if (data.isSuccess) {
      onSuccessRef.current &&
        onSuccessRef.current(data as QueryObserverSuccessResult<TSelected, TError>);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.isSuccess, data.dataUpdatedAt]);

  return data;
};